import { isArray, isEqual, isPlainObject, mergeWith, omit } from 'lodash-es';
import { twJoin } from 'tailwind-merge';

type Customizer = (a: any, b: any, key?: string, object?: object, source?: object) => any;

type Merge = <A, B, C, D, E>(a?: A, b?: B, c?: C, d?: D, e?: E) => A & B & C & D & E;

const withCustomizers =
  (...customizers: Customizer[]): Customizer =>
  (a, b, key, object, source) => {
    for (const customizer of customizers) {
      const value = customizer(a, b, key, object, source);
      if (value !== undefined) {
        return value;
      }
    }
  };

const classNameCustomizer: Customizer = (a, b, key) => {
  if (key === 'className') {
    return twJoin(a, b);
  }
};

const arrayCustomizer: Customizer = (a, b) => {
  if (isArray(b)) {
    return b;
  }
};

const stableCustomizer: Customizer = (a, b) => {
  if (isEqual(a, b)) {
    return a;
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const result: Record<string, unknown> = { ...a };

    for (const key in b) {
      const A = a[key as keyof typeof a];
      const B = b[key as keyof typeof b];
      result[key] = Object.hasOwn(a, key) ? stableCustomizer(A, B) : B;
    }

    return result;
  }

  return b;
};

export const merge: Merge = <A, B, C, D, E>(a?: A, b?: B, c?: C, d?: D, e?: E) => {
  const value = mergeWith(a, b, c, d, e, () => {});
  return (value ?? {}) as A & B & C & D & E;
};

export const mergeStable: Merge = <A, B, C, D, E>(a?: A, b?: B, c?: C, d?: D, e?: E) => {
  const value = mergeWith(a, b, c, d, e, withCustomizers(stableCustomizer));
  return (value ?? {}) as A & B & C & D & E;
};

export const mergeOptions: Merge = <A, B, C, D, E>(a?: A, b?: B, c?: C, d?: D, e?: E) => {
  const value = mergeWith(a, b, c, d, e, withCustomizers(classNameCustomizer, arrayCustomizer));
  return (value ?? {}) as A & B & C & D & E;
};

export const mergeProps: Merge = <A, B, C, D, E>(a?: A, b?: B, c?: C, d?: D, e?: E) => {
  const keys = ['animation', 'colors', 'size', 'variant', 'theme'];
  const value = mergeWith(a, b, c, d, e, withCustomizers(classNameCustomizer, arrayCustomizer));
  return omit(value ?? {}, keys) as A & B & C & D & E;
};
